import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "../../../../components/modals/BaseModal";
import { ComunicacionAjax } from "../../../../ts/api/comunicacion";
import { useCallback, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { TypedTable } from "../../../../components/TypedTable";
import { notificacionError, notificacion } from "../../../../components/Alerts";
import type { Respuesta } from "../../../../ts/typings/clases";
import { useDisclosureData } from "../../../../hooks/useModal";
import { Loading, QueryError } from "../../../../components/Loading/Loading";
import { TablaArticulo } from "./TablaArticulos";

interface ModalEditarUbicacionProps extends BaseModalProps {
	readonly idUbicacion: string | null;
	readonly zona: string;
	readonly fila: string;
}
export function ModalEditarUbicacion(props: ModalEditarUbicacionProps) {
	const queryClient = useQueryClient();


	const eliminarUbicacion = (id: string) => {
		if (!confirm("¿Está seguro de que desea eliminar la ubicación?")) {
			console.debug("Peticion cancelada por el usuario");
			return;
		}

		const parametro = {
			"id": id,
		};

		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/api/v1/eliminarUbicacion", parametro)
			.then(respuesta => {
				if (respuesta.tipo == "error") {
					notificacionError(respuesta.message ?? "Error al eliminar la ubicación.");
					return;
				}

				notificacion(respuesta.message ?? "Ubicación eliminada con éxito.");
				void queryClient.invalidateQueries({ queryKey: ["getEstanterias"] });
				void queryClient.invalidateQueries({ queryKey: ["getEstanteria", data?.idAlmacen, data?.idFila] });


				props.close();
			});
	};



	const { data, error, isPending } = useDatosUbicacion(props.idUbicacion);
	const { data: contenidoData, error: contenidoError, isPending: contenidoPending } = useContenidoUbicacion(props.idUbicacion);

	const hasArticulos = (contenidoData?.articulos.length ?? 0) > 0;
	const hasContenedores = (contenidoData?.contenedores.length ?? 0) > 0;

	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Ubicación {props.idUbicacion}</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body" style={{ minWidth: "75vw" }}>
				{(isPending == true || contenidoPending == true) && <Loading />}

				{(error != null || contenidoError != null) && (
					<QueryError error={error ?? contenidoError} fallback="Error al cargar los datos de la ubicación" />
				)}

				{data != null && contenidoData != null && props.idUbicacion != null && (
					<CuerpoModal idUbicacion={props.idUbicacion} zona={props.zona} fila={props.fila} data={data} contenido={contenidoData} />
				)}
			</div>

			<div className="modal-footer">
				<button type="button" className="btnSecondary" style={{ float: "left" }} onClick={props.close}>Cerrar</button>
				<p style={{ color: "red" }} >
					{hasArticulos || hasContenedores ? "No se puede eliminar la ubicación porque tiene artículos asignados" : ""}
				</p>
				<button type="button" className="btnSecondary" onClick={() => eliminarUbicacion(props.idUbicacion ?? "")} disabled={hasArticulos || hasContenedores}>Eliminar</button>
			</div>
		</BaseModal>
	);
}

function CuerpoModal(props: { readonly idUbicacion: string; readonly zona: string; readonly fila: string; readonly data: DatosUbicacion; readonly contenido: ContenidoUbicacion; }) {
	const editarArtFijo = useDisclosureData<ArticuloFijo | null>(null);

	return (
		<div style={{ display: "flex", gap: 20 }}>
			<div style={{ flex: 2 }}>
				<h1 style={{ margin: 0 }}>Artículos fijos</h1>

				<TablaFijos
					data={props.data.articulosFijos}
					idUbicacion={props.idUbicacion}
					zona={props.zona}
					fila={props.fila}
					openEditar={editarArtFijo.open}
				/>
				{editarArtFijo.isOpen ? (
					<FormArticuloFijo
						articulo={editarArtFijo.data}
						close={editarArtFijo.close}
						idUbicacion={props.idUbicacion}
						zona={props.zona}
						fila={props.fila}
					/>
				) : (
					<div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
						<button type="button" className="btnPrimary" onClick={() => editarArtFijo.open(null)}>
							Nuevo artículo
						</button>
					</div>
				)}

			</div>

			<div style={{ flex: 3 }}>
				<h1 style={{ margin: 0 }}>Artículos en la ubicación</h1>
				<TablaArticulo data={props.contenido} />
			</div>



		</div>
	);
}


function TablaFijos(props: { readonly data: ArticuloFijo[]; readonly idUbicacion: string; readonly zona: string; readonly fila: string; readonly openEditar: (art: ArticuloFijo) => void; }) {
	const { openEditar } = props;
	const queryClient = useQueryClient();

	const eliminarArticuloFijo = useCallback((id: number) => {
		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/api/v1/eliminarArticuloFijo", { id }).then((respuesta) => {
			if (respuesta.tipo == "error") {
				notificacionError(respuesta.message ?? "Error al eliminar el artículo fijo.");
				return;
			}

			notificacion("Articulo eliminado");
			void queryClient.invalidateQueries({ queryKey: ["getDatosUbicacion", props.idUbicacion] });
			void queryClient.invalidateQueries({ queryKey: ["getEstanteria", props.zona, props.fila] });
		});
	}, [props.fila, props.idUbicacion, props.zona, queryClient]);



	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<ArticuloFijo>();

		const getAccionArticuloFijo = (linea: ArticuloFijo) => {
			return (
				<div className="clickable" style={{ display: "flex", justifyContent: "space-around" }}>
					<a target="_self" className="main-color btnIcon" onClick={() => openEditar(linea)}>
						<i className="fa fa-pen-to-square" style={{ fontSize: 25, margin: "0 10px" }} />
					</a>
					<a target="_self" className="main-color btnIcon" onClick={() => eliminarArticuloFijo(linea.id)}>
						<i className="fa fa-trash" style={{ fontSize: 25, margin: "0 10px" }} />
					</a>
				</div>
			);
		};

		return [
			columnHelper.accessor("idArticulo", { header: "Artículo", meta: { className: "dt-center" } }),
			columnHelper.accessor("minimo", { header: "Mínimo", meta: { className: "dt-center" } }),
			columnHelper.accessor("maximo", { header: "Máximo", meta: { className: "dt-center" } }),
			columnHelper.display({ id: "acciones", header: "", meta: { className: "dt-center" }, cell: (info) => getAccionArticuloFijo(info.row.original) }),
		];
	}, [eliminarArticuloFijo, openEditar]);

	return <TypedTable
		className="dataTable display"
		data={props.data}
		columns={columns}
		noDataMessage="No hay artículos fijos asignados"
	/>;
}

interface FormArticuloFijoProps {
	readonly close: () => void;
	readonly idUbicacion: string;
	readonly articulo: ArticuloFijo | null;
	readonly zona: string; readonly fila: string;
}
function FormArticuloFijo(props: FormArticuloFijoProps) {
	const queryClient = useQueryClient();

	const guardarArticuloFijo = (e: React.FormEvent) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);

		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/api/v1/modificarArticuloFijo", {
			xarticulo_id: formData.get("idArticuloFijo"),
			ubicacion_id: props.idUbicacion,
			minimo: formData.get("minimoFijo"),
			maximo: formData.get("maximoFijo"),
		})
			.then((response) => {
				if (response.tipo == "error") {
					notificacionError(response.message ?? "Error al guardar el artículo fijo.");
					return;
				}

				void queryClient.invalidateQueries({ queryKey: ["getDatosUbicacion", props.idUbicacion] });
				void queryClient.invalidateQueries({ queryKey: ["getEstanteria", props.zona, props.fila] });
				props.close();
			});
	};

	return (
		<form onSubmit={guardarArticuloFijo} key={props.articulo?.id ?? "new"}>
			<div style={{ display: "grid", gridAutoFlow: "column", gap: 10, marginTop: 10 }} >
				<div>
					<input type="text" className="inputsForm" name="idArticuloFijo" defaultValue={props.articulo?.idArticulo ?? ""} placeholder="Artículo" />
				</div>

				<div>
					<input type="number" className="inputsForm" name="minimoFijo" defaultValue={props.articulo?.minimo ?? ""} placeholder="Mínimo" />
				</div>

				<div>
					<input type="number" className="inputsForm" name="maximoFijo" defaultValue={props.articulo?.maximo ?? ""} placeholder="Máximo" />
				</div>

				<div style={{ display: "flex", gap: "5px" }} >
					<button type="submit" className="btnIcon primary">
						<i className="fa fa-floppy-disk" style={{ fontSize: 25 }} />
					</button>
					<button type="button" className="btnIcon" onClick={props.close}>
						<i className="fa fa-xmark" style={{ fontSize: 25 }} />
					</button>
				</div>

			</div>

		</form>
	);
}


function useDatosUbicacion(id: string | null) {
	return useQuery<DatosUbicacion | null>({
		queryKey: ["getDatosUbicacion", id],
		queryFn: () => {
			if (id == null) return Promise.resolve(null);
			return ComunicacionAjax.sendAjaxRequest<DatosUbicacion>("GET", "/api/v1/getDatosUbicacion", { "id": id, });
		}
	});
}
interface DatosUbicacion {
	idUbicacion: string;
	idAlmacen: string;
	idFila: string;
	idColumna: string;
	idAltura: string;
	articulosFijos: ArticuloFijo[],
}
interface ArticuloFijo {
	id: number,
	idUbicacion: number,	// Este es el ID de la FILA, no lo que solemos llamar id ubicacion
	idArticulo: string,
	maximo: number,
	minimo: number,
}

export function useContenidoUbicacion(id: string | null) {
	return useQuery<ContenidoUbicacion>({
		queryKey: ["getContenidoUbicacion", id],
		queryFn: () => {
			if (id == null) return Promise.resolve({ articulos: [], contenedores: [] });

			return ComunicacionAjax.sendAjaxRequest<ContenidoUbicacion>(
				"GET",
				"/api/v1/getContenidoUbicacion",
				{ "idUbicacion": id, "includeContainer": true }
			);
		}
	});
}
export interface ContenidoUbicacion {
	articulos: ArticuloUbi[],
	contenedores: ContenedorUbi[],
}
export interface ArticuloUbi {
	tipo: "A";
	id: string;
	idFase: number;
	lote: string;

	ubicacion: string;
	contenedor: number;

	cantidad: number;
	descripcion: string;
}
export interface ContenedorUbi {
	tipo: "C";
	id: number;
	ubicacion: string;
	esVolatil: boolean;

	articulos: ArticuloUbi[];
}